import classNames from "classnames";
import { useMemo } from "react";
import { Recipient } from "react-migration/domains/sites/typings/Recipient";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSnapshot } from "valtio";
import Feature from "src/js/stores/user/Feature";
import hasFeature from "src/js/stores/user/actions/hasFeature";

import { Placeholder } from "./Placeholder";
import { letterStore } from "src/js/stores/letter/store";

const AddressLine = ({ value }: { value: string | undefined }) => {
  return (
    <>
      {value && (
        <>
          {value}
          <br />
        </>
      )}
    </>
  );
};

interface HeadingProps {
  showValues?: boolean;
}

export const Heading = ({ showValues }: HeadingProps) => {
  const usAccess = hasFeature(Feature.usAccess);
  const { t } = useTranslation();
  const {
    recipients,
    letter: { senderAddressLine1, senderCity, senderName, senderPostcode, senderProvince, logo },
    activeRecipientId,
  } = useSnapshot(letterStore);

  const { fullName, addressLine1, city, region, postCode } =
    recipients?.find((recipient: Recipient) => recipient._id === activeRecipientId) || {};

  const addressClassName = useMemo(() => {
    const a4ClassName = classNames("w-[90mm]", "h-[45mm]", "mt-[19mm]");
    const letterClassName = classNames("w-[3.25in]", "h-[2.3in]", "mt-[-0.28in]");
    return classNames("flex", usAccess ? letterClassName : a4ClassName);
  }, [usAccess]);

  const addressSection = useMemo(() => {
    return usAccess ? (
      <div className={classNames(addressClassName, "flex", "flex-col", "justify-between")}>
        <div>
          <AddressLine value={senderName} />
          <AddressLine value={senderAddressLine1} />
          <AddressLine value={`${senderCity}, ${senderProvince} ${senderPostcode}`} />
        </div>
        <div>
          <AddressLine value={fullName} />
          <AddressLine value={addressLine1} />
          <AddressLine value={`${city}, ${region} ${postCode}`} />
        </div>
      </div>
    ) : (
      <div className={addressClassName}>
        <AddressLine value={fullName} />
        <AddressLine value={addressLine1} />
        <AddressLine value={city} />
        <AddressLine value={region} />
        <AddressLine value={postCode} />
      </div>
    );
  }, [
    addressClassName,
    addressLine1,
    city,
    fullName,
    postCode,
    region,
    senderAddressLine1,
    senderCity,
    senderName,
    senderPostcode,
    senderProvince,
    usAccess,
  ]);

  return (
    <div className={classNames("flex", "justify-between")} data-testid="letter-preview-heading">
      {showValues ? (
        addressSection
      ) : (
        <Placeholder className={classNames(addressClassName)}>
          <div className={classNames("flex", "flex-col", "items-center", "justify-center")}>
            {t("sites.letters.recipient_address")}
          </div>
        </Placeholder>
      )}
      {showValues || logo?.url ? (
        <div
          className={classNames(
            "flex",
            "items-center",
            "justify-center",
            "h-[156px]",
            "w-[156px]",
            "bg-contain",
            "overflow-hidden"
          )}
        >
          <img className={classNames("h-auto", "w-full", "object-center")} src={logo?.url} />
        </div>
      ) : (
        <Placeholder className={classNames("h-[156px]", "w-[156px]")}>
          <div className={classNames("flex", "flex-col", "items-center", "justify-center")}>
            <i className="icon-lt-image-line text-5xl text-neutral-400" />
            {t("sites.letters.logo")}
          </div>
        </Placeholder>
      )}
    </div>
  );
};
